import { useCallback, useRef, useState } from 'react';
import { AlertBox, Box, Button, FlexBox, Icon, Typography } from '@vp/swan';
import { useChatActions, useChatMessages } from '../contexts/ChatContext';
import { AiAvatar } from './AiAvatar';
import { ChatErrorBanner } from './ChatErrorBanner';
import { GlobalChatBriefStyles } from './GlobalStyles';
import { LandingHero } from './LandingHero';
import { MessageBubble } from './MessageBubble';

// How many pixels from the bottom before the scroll button appears.
const SCROLL_BUTTON_THRESHOLD = 100;

interface ChatMessageListProps {
    onReview?: () => void;
    onCtaLinkClick?: (url: string, label: string) => void;
}

export function ChatMessageList({ onReview, onCtaLinkClick }: ChatMessageListProps) {
    const {
        messages,
        isLoading,
        error,
        isStaleSession,
        isFatalError,
        retryCount,
        hasUserSent,
        messagesEndRef,
    } = useChatMessages();
    const { onRetry } = useChatActions();

    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const [showScrollButton, setShowScrollButton] = useState(false);

    const handleScroll = useCallback(() => {
        const el = scrollContainerRef.current;
        if (!el) return;
        const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
        setShowScrollButton(distanceFromBottom > SCROLL_BUTTON_THRESHOLD);
    }, []);

    const scrollToBottom = useCallback(() => {
        const el = scrollContainerRef.current;
        if (!el) return;
        el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    }, []);

    // Component-request messages are rendered in ChatInputArea, not as bubbles
    const filtered = messages.filter((msg) => msg.type !== 'componentRequest');
    // Hero stays visible until the user actually engages — a seeded greeting
    // alone shouldn't collapse it.
    const heroCollapsed = hasUserSent;

    return (
        <Box
            ref={scrollContainerRef}
            onScroll={handleScroll}
            style={{
                flex: 1,
                minHeight: 0,
                overflowY: 'auto',
                position: 'relative',
                backgroundColor: 'var(--swan-sem-color-bg-info)',
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
            }}
            className="hide-scrollbar"
        >
            <Box
                role="log"
                aria-live="polite"
                aria-label="Chat messages"
                style={{
                    maxWidth: '768px',
                    margin: '0 auto',
                    paddingTop: 'var(--swan-sem-space-6)',
                    paddingBottom: 'var(--swan-sem-space-6)',
                    paddingLeft: 'var(--swan-sem-space-5)',
                    paddingRight: 'var(--swan-sem-space-5)',
                }}
            >
                {/* Landing hero — fades out once messages exist */}
                <Box
                    style={{
                        opacity: heroCollapsed ? 0 : 1,
                        transition: 'opacity 300ms ease-in-out',
                        pointerEvents: heroCollapsed ? 'none' : 'auto',
                        position: heroCollapsed ? 'absolute' : 'relative',
                        ...(heroCollapsed ? { height: 0, overflow: 'hidden' } : {}),
                    }}
                >
                    <GlobalChatBriefStyles />
                    <LandingHero />
                </Box>

                {filtered.map((message, index) => (
                    <MessageBubble
                        key={index}
                        message={message}
                        animate={index === filtered.length - 1 && message.role !== 'user'}
                        onCtaLinkClick={onCtaLinkClick}
                    />
                ))}

                {isLoading && (
                    <FlexBox
                        justifyContent="flex-start"
                        style={{ marginBottom: 'var(--swan-sem-space-5)' }}
                        role="status"
                        aria-label="Assistant is thinking"
                    >
                        <AiAvatar animated />
                    </FlexBox>
                )}

                {!isLoading && isFatalError && error && (
                    <AlertBox
                        skin="error"
                        toast={false}
                        style={{ marginBottom: 'var(--swan-sem-space-5)' }}
                    >
                        <Typography>{error}</Typography>
                    </AlertBox>
                )}

                {!isLoading && isStaleSession && (
                    <AlertBox
                        skin="warning"
                        toast={false}
                        style={{ marginBottom: 'var(--swan-sem-space-5)' }}
                    >
                        <FlexBox alignItems="center" justifyContent="space-between">
                            <Typography>
                                Your chat session is out of date. Please refresh the page to
                                proceed.
                            </Typography>
                            <Button
                                skin="link"
                                iconPosition="left"
                                onClick={() => window.location.reload()}
                            >
                                <Icon iconType="reset" size="small" mr="3" />
                                Refresh page
                            </Button>
                        </FlexBox>
                    </AlertBox>
                )}

                {!isLoading && !isFatalError && !isStaleSession && error && (
                    <ChatErrorBanner
                        retryCount={retryCount}
                        onRetry={onRetry}
                        onReview={onReview}
                    />
                )}

                <div ref={messagesEndRef} />
            </Box>

            {/* Scroll-to-bottom button — visible when the user has scrolled up */}
            <button
                type="button"
                onClick={scrollToBottom}
                aria-label="Scroll to latest message"
                style={{
                    position: 'sticky',
                    bottom: 'var(--swan-sem-space-5)',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '36px',
                    height: '36px',
                    padding: 0,
                    borderRadius: 'var(--swan-sem-border-radius-standard)',
                    border: '1px solid var(--swan-sem-color-border-standard)',
                    backgroundColor: 'var(--swan-sem-color-bg-standard)',
                    cursor: 'pointer',
                    opacity: showScrollButton ? 1 : 0,
                    transition: 'opacity 200ms ease-in-out',
                    pointerEvents: showScrollButton ? 'auto' : 'none',
                    zIndex: 10,
                }}
            >
                <Icon iconType="arrowDown" size="20p" />
            </button>
        </Box>
    );
}
